<template>

  <v-number-input
    prefix="$"
    :precision="2"
    :label="`${channel?.title ?? model.channelFacility.channel} (${model.channelFacility.ID})`"
    v-model="formState.channelCost"
    clearable
    :rules="rules"
    :density="density"
    :disabled="disabled"
    :hide-details="hideDetails"
  >

    <template v-if="showId" v-slot:prepend-inner>
      <div class="text-disabled text-body-2 mr-2">
        CDR<br>
        {{ model.id }}
      </div>
    </template>

  </v-number-input>

</template>

<script setup lang="ts">

/**
 * Field for editing a single ChannelDefaultRate.
 */

import { computed, reactive, watch } from 'vue'
import ChannelRegistry from '../../../channels/ChannelRegistry'
import { dollarAmount, minValue } from '../../../validation'

import type { ChannelDefaultRateFieldState } from '../../../types/misc'
import AbstractChannel from '../../../channels/AbstractChannel'

interface Props {
  clearable?: boolean
  showId?: boolean
  defaultCost: null | number
  disabled?: boolean
  density?: null | 'compact' | 'default' | 'comfortable'
  hideDetails?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  clearable: true,
  showId: true,
  disabled: false,
  channelDefaultRateId: null,
  density: null,
  hideDetails: false,
})

const model = defineModel<ChannelDefaultRateFieldState>({required: true})

const channel = computed<AbstractChannel|undefined>(() => {
  return ChannelRegistry.find(model.value.channelFacility.channel)
})

/**
 * Form state and rules
 */

interface FormState {
  channelCost: null | number
}

const formState = reactive<FormState>({
  channelCost: (
    model.value.channelCost === props.defaultCost ? null :
    model.value.channelCost
  ),
})

const rules = [
  dollarAmount(),
  minValue(0.01),
  (value: any) => {
    if (model.value.channelFacility?.channel === 'spothero' && value > 250) {
      return 'SpotHero rates can\'t exceed $250'
    }
    return true
  },
]

watch(() => formState.channelCost, () => {
  model.value.channelCost = formState.channelCost ?? props.defaultCost
})

// if the default cost is changed, and this CDR is inheriting the default cost, inherit the new value
watch(() => props.defaultCost, (newDefaultCost, oldDefaultCost) => {
  if (model.value.channelCost === oldDefaultCost) {
    model.value.channelCost = newDefaultCost
  }
})

</script>
